import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"

/**
 * Canonical framework code (backend `mc_framework_codes.normalize_framework_code`,
 * e.g. "reach_svhc", "rohs") -> the translation catalog key for its short display
 * label. Shared by every render site that gets a `framework_code`/`framework_codes`
 * field from the API — the declarations list, the supplier/customer active-work
 * cards, and the conformity list — so the German label is defined ONCE, not
 * reinvented per screen.
 *
 * Render sites that only get a RAW framework string (a CMS slug, an
 * UPPER_SNAKE token, a `canonical_id` — never a server-normalized code) go
 * through `normalizeFrameworkCode()` below first, then this same map — see
 * that function's doc for why a second alias table lives in this file too.
 */
/** Own-property lookup: the raw framework strings come off the wire, and a value
 *  like "constructor" or "__proto__" must not resolve to an inherited member of a
 *  plain-object map. */
function own<V>(map: Record<string, V>, key: string): V | undefined {
  return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined
}

const REGULATION_LABEL_KEYS: Record<string, TranslationKeys> = {
  reach_svhc: "MC_REG_LABEL_REACH_SVHC",
  rohs: "MC_REG_LABEL_ROHS",
  reach_annex_xiv: "MC_REG_LABEL_REACH_ANNEX_XIV",
  reach_annex_xvii: "MC_REG_LABEL_REACH_ANNEX_XVII",
  pfas: "MC_REG_LABEL_PFAS",
  elv: "MC_REG_LABEL_ELV",
  china_rohs_2: "MC_REG_LABEL_CHINA_ROHS_2",
  pops: "MC_REG_LABEL_POPS",
  tsca: "MC_REG_LABEL_TSCA",
  prop65: "MC_REG_LABEL_PROP65",
  halogen_free: "MC_REG_LABEL_HALOGEN_FREE",
  fmd: "MC_REG_LABEL_FMD",
  scip: "MC_REG_LABEL_SCIP",
}

/**
 * One regulation's display label. `code` missing, empty, or not in the map above
 * (a supplier's own wording the backend could not normalize) -> `fallback`
 * unchanged, so the server's English string is always shown rather than nothing.
 */
export function regulationLabel(
  code: string | null | undefined,
  fallback: string,
  translate: (key: TranslationKeys) => string,
): string {
  if (!code) return fallback
  const key = own(REGULATION_LABEL_KEYS, code)
  return key ? translate(key) : fallback
}

/**
 * A list of regulation labels, one per `fallbacks` entry (the server's own
 * per-item strings — e.g. a declaration's split "Verordnung" labels, or an
 * inquiry's `frameworks`). `codes` may be shorter than `fallbacks` (a legacy
 * single-value `framework_codes` list, or a row from a build that predates the
 * field) — a missing index just falls back to that entry's own label, same as a
 * missing/unmapped code.
 */
export function regulationLabels(
  codes: ReadonlyArray<string | null | undefined> | null | undefined,
  fallbacks: readonly string[],
  translate: (key: TranslationKeys) => string,
): string[] {
  return fallbacks.map((fallback, i) =>
    regulationLabel(codes?.[i], fallback, translate),
  )
}

/**
 * Frontend mirror of backend/turnus_ai/mc_framework_codes.py's
 * `normalize_framework_code()` — for the render sites that only get a RAW
 * framework string (a CMS slug like "reach-svhc", an UPPER_SNAKE token like
 * "REACH_ANNEX_XVII"/`mc_regulations.name`, or a question-taxonomy
 * `canonical_id` like "us_tsca_pbt") and never a server-normalized
 * `framework_code`. Maps any of those to the SAME closed set of lowercase
 * codes `REGULATION_LABEL_KEYS` is keyed on, so `regulationLabel()` /
 * `regulationShortLabel()` can be reused instead of every call site
 * inventing its own alias table.
 *
 * Kept in sync BY HAND with the backend module (this file's own alias table
 * intentionally omits the backend's third, older "display-label" vocabulary —
 * free-text English phrases like "EU RoHS" — no frontend render site hands
 * this function that shape). A raw value added on the backend with no
 * matching alias here just means the caller's own fallback string is shown
 * instead of the canonical translated label — never a crash or a wrong one.
 */
const FRAMEWORK_CODE_ALIASES: Record<string, string> = {
  // CMS hyphenated slugs (mc_tools/regulatory_content.py's FRAMEWORK_BRIDGE)
  "reach-svhc": "reach_svhc",
  "reach-annex14": "reach_annex_xiv",
  "reach-annex17": "reach_annex_xvii",
  rohs: "rohs",
  "china-rohs": "china_rohs_2",
  pfas: "pfas",
  pop: "pops",
  tsca: "tsca",
  cp65: "prop65",
  "halogen-free": "halogen_free",
  // UPPER_SNAKE tokens (ConformityFramework.key / FRAMEWORK_BRIDGE token /
  // mc_regulations.name)
  REACH_SVHC: "reach_svhc",
  ROHS: "rohs",
  REACH_ANNEX_XIV: "reach_annex_xiv",
  REACH_ANNEX_XVII: "reach_annex_xvii",
  PFAS: "pfas",
  ELV: "elv",
  CHINA_ROHS_2: "china_rohs_2",
  POP: "pops",
  POPS: "pops",
  TSCA: "tsca",
  PROP65: "prop65",
  PROP_65: "prop65",
  HALOGEN_FREE: "halogen_free",
  FMD: "fmd",
  SCIP: "scip",
  // The SVHC candidate-list row's own `mc_regulations.name` is bare "REACH".
  REACH: "reach_svhc",
  SVHC: "reach_svhc",
  // question-taxonomy canonical_ids not already spelled like their canonical
  // code (the ones that are — "reach_svhc", "elv", "fmd", "scip", "pfas",
  // "rohs", "reach_annex_xiv"/"xvii", "china_rohs_2" — never need an alias:
  // `normalizeFrameworkCode` checks the canonical set itself first).
  us_tsca_pbt: "tsca",
  eu_pop: "pops",
}

// Case-insensitive fallback, mirroring the backend's own — the pre-Orion
// `McDeclaration.framework` legacy column holds mixed casing a real registry
// never produces ("RoHS", not "ROHS"/"rohs").
const FRAMEWORK_CODE_ALIASES_UPPER: Record<string, string> = Object.fromEntries(
  Object.entries(FRAMEWORK_CODE_ALIASES).map(([raw, code]) => [
    raw.toUpperCase(),
    code,
  ]),
)

/**
 * Any of the raw framework vocabularies above -> the canonical lowercase
 * code, or `null` when `raw` is empty or names something this map does not
 * (yet) cover. Never throws — an unmapped value is the expected, honest case
 * for a raw string this map hasn't caught up with, not a bug.
 */
export function normalizeFrameworkCode(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null
  const cleaned = raw.trim()
  if (!cleaned) return null
  if (own(REGULATION_LABEL_KEYS, cleaned)) return cleaned
  const code = own(FRAMEWORK_CODE_ALIASES, cleaned)
  if (code) return code
  return own(FRAMEWORK_CODE_ALIASES_UPPER, cleaned.toUpperCase()) ?? null
}

// The handful of codes whose SHORT badge text (GapsStep's framework chips)
// is not just MC_REG_LABEL_*'s own text — that catalog favours a full,
// disambiguating name ("REACH SVHC (Candidate List, Art. 33)", "EU RoHS
// (2011/65/EU)") that does not fit a badge. Every code left out of this map
// already has a short-enough MC_REG_LABEL_* text (PFAS, ELV, FMD, SCIP, POPs,
// TSCA) — `regulationShortLabel` falls through to it rather than duplicating
// an identical key here.
const REGULATION_SHORT_LABEL_KEYS: Partial<Record<string, TranslationKeys>> = {
  reach_svhc: "MC_REG_SHORT_REACH_SVHC",
  rohs: "MC_REG_SHORT_ROHS",
  reach_annex_xiv: "MC_REG_SHORT_REACH_ANNEX_XIV",
  reach_annex_xvii: "MC_REG_SHORT_REACH_ANNEX_XVII",
  china_rohs_2: "MC_REG_SHORT_CHINA_ROHS_2",
  prop65: "MC_REG_SHORT_PROP65",
}

/**
 * The SHORT form of a regulation's display label, for badges too narrow for
 * `regulationLabel()`'s full text (GapsStep's per-gap framework chip). Same
 * fallback contract as `regulationLabel`: `code` missing/unmapped ->
 * `fallback` unchanged.
 */
export function regulationShortLabel(
  code: string | null | undefined,
  fallback: string,
  translate: (key: TranslationKeys) => string,
): string {
  if (!code) return fallback
  const shortKey = own(REGULATION_SHORT_LABEL_KEYS, code)
  if (shortKey) return translate(shortKey)
  return regulationLabel(code, fallback, translate)
}

// One line saying what a regulation covers, for hover cards that already show its
// name (the dashboard tiles). Hover-only: the labels everywhere else stay as they are.
const REGULATION_DESCRIPTION_KEYS: Partial<Record<string, TranslationKeys>> = {
  reach_svhc: "MC_REG_DESC_REACH_SVHC",
  reach_annex_xiv: "MC_REG_DESC_REACH_ANNEX_XIV",
  reach_annex_xvii: "MC_REG_DESC_REACH_ANNEX_XVII",
  rohs: "MC_REG_DESC_ROHS",
  pops: "MC_REG_DESC_POPS",
  pfas: "MC_REG_DESC_PFAS",
  elv: "MC_REG_DESC_ELV",
  china_rohs_2: "MC_REG_DESC_CHINA_ROHS_2",
  tsca: "MC_REG_DESC_TSCA",
  prop65: "MC_REG_DESC_PROP65",
  halogen_free: "MC_REG_DESC_HALOGEN_FREE",
}

/** What a regulation covers in one line, or null for a code without one. */
export function regulationDescription(
  code: string | null | undefined,
  translate: (key: TranslationKeys) => string,
): string | null {
  const key = code ? own(REGULATION_DESCRIPTION_KEYS, code) : undefined
  return key ? translate(key) : null
}

/**
 * Short labels for the framework keys the backend returns for an inquiry's scope
 * (`asked_regulations`: `REACH_SVHC`, `PFAS`, …), in the order given.
 */
export function regulationKeyShortLabels(
  keys: readonly string[],
  translate: (key: TranslationKeys) => string,
): string[] {
  return keys.map((key) =>
    regulationShortLabel(normalizeFrameworkCode(key), key, translate),
  )
}

/**
 * Options for the master-data regulation filters (article and product lists). The
 * server sends the org's Settings › Regelwerke set first, then the rest of the
 * catalogue; each option carries its group, so the filter draws a divider between the
 * two (also while searching). Each is labelled from its canonical framework key, else
 * the catalogue row's own name.
 */
export function regulationFilterOptions(
  regulations: ReadonlyArray<{
    id: string
    name: string
    frameworkKey?: string | null
    inOrgSet?: boolean
  }>,
  translate: (key: TranslationKeys) => string,
): { value: string; label: string; group: "org" | "catalogue" }[] {
  return regulations.map((r) => ({
    value: r.id,
    label: regulationLabel(
      normalizeFrameworkCode(r.frameworkKey ?? r.name),
      r.name,
      translate,
    ),
    group: r.inOrgSet ? "org" : "catalogue",
  }))
}
